import React, { useState } from 'react';
import { Table, Form, Button, Badge, Modal, Alert as BootstrapAlert } from 'react-bootstrap';
import { useFormats } from '../../context/formatContext';

const ClubMapper = ({ clubMappings, detectedClubColumn, onChange }) => {
  const { standardClubs } = useFormats();
  const [showCustomClubModal, setShowCustomClubModal] = useState(false);
  const [customClubName, setCustomClubName] = useState('');
  const [customClubType, setCustomClubType] = useState('irons');

  // Flatten club codes for dropdown
  const allClubCodes = standardClubs
    ? [
        ...standardClubs.woods,
        ...standardClubs.hybrids,
        ...standardClubs.irons,
        ...standardClubs.wedges
      ]
    : [];

  const handleStandardNameChange = (index, newName) => {
    const updated = [...clubMappings];
    updated[index] = {
      ...updated[index],
      standardName: newName
    };
    onChange(updated);
  };

  const handleCreateCustomClub = () => {
    if (!customClubName.trim()) return;

    // For now, just add it to the dropdown (in a real implementation, this would go to backend)
    // Close modal and select it for the current mapping
    setShowCustomClubModal(false);
    setCustomClubName('');
    alert(`Custom club "${customClubName}" added. Note: This feature requires backend implementation to persist custom clubs.`);
  };

  const unmappedCount = clubMappings.filter(m => !m.standardName).length;

  return (
    <div>
      {detectedClubColumn && (
        <BootstrapAlert variant="info">
          <strong>Club Column Detected:</strong> Using "{detectedClubColumn}" column for club identification.
          All unique values from this column are listed below.
        </BootstrapAlert>
      )}

      {unmappedCount > 0 && (
        <BootstrapAlert variant="warning">
          <strong>Action Required:</strong> {unmappedCount} club(s) still need to be mapped to standard codes.
          All clubs must be mapped before saving.
        </BootstrapAlert>
      )}

      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th style={{ width: '50%' }}>Original Club Name (from CSV)</th>
            <th style={{ width: '50%' }}>Standard Code *</th>
          </tr>
        </thead>
        <tbody>
          {clubMappings.length === 0 ? (
            <tr>
              <td colSpan="2" className="text-center text-muted">
                No clubs detected in the sample file.
              </td>
            </tr>
          ) : (
            clubMappings.map((mapping, index) => (
              <tr key={index} className={!mapping.standardName ? 'table-warning' : ''}>
                <td>
                  <strong>{mapping.originalName}</strong>
                  {!mapping.standardName && (
                    <Badge bg="danger" className="ms-2">Unmapped</Badge>
                  )}
                </td>
                <td>
                  <Form.Select
                    size="sm"
                    value={mapping.standardName}
                    onChange={(e) => handleStandardNameChange(index, e.target.value)}
                    className={!mapping.standardName ? 'border-warning' : ''}
                  >
                    <option value="">-- Select Standard Code --</option>
                    <optgroup label="Woods">
                      {standardClubs?.woods?.map(code => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Hybrids">
                      {standardClubs?.hybrids?.map(code => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Irons">
                      {standardClubs?.irons?.map(code => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Wedges">
                      {standardClubs?.wedges?.map(code => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </optgroup>
                  </Form.Select>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <div className="d-flex gap-2">
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => setShowCustomClubModal(true)}
          title="Add a custom club code (requires admin approval)"
        >
          + Request New Club Code
        </Button>
      </div>

      <div className="mt-3 p-3 bg-warning bg-opacity-10 border border-warning rounded">
        <small>
          <strong>Important:</strong> All clubs found in the sample file MUST be mapped to standard codes.
          This ensures data consistency across the system. If you need a club code that doesn't exist,
          click "Request New Club Code" above.
        </small>
      </div>

      {standardClubs && (
        <div className="mt-3 p-3 bg-light border rounded">
          <small>
            <strong>Available Standard Club Codes:</strong>
            <div className="row mt-2">
              <div className="col-md-3">
                <strong>Woods:</strong> {standardClubs.woods?.join(', ')}
              </div>
              <div className="col-md-3">
                <strong>Hybrids:</strong> {standardClubs.hybrids?.join(', ')}
              </div>
              <div className="col-md-3">
                <strong>Irons:</strong> {standardClubs.irons?.join(', ')}
              </div>
              <div className="col-md-3">
                <strong>Wedges:</strong> {standardClubs.wedges?.join(', ')}
              </div>
            </div>
          </small>
        </div>
      )}

      {/* Custom Club Modal */}
      <Modal show={showCustomClubModal} onHide={() => setShowCustomClubModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Request New Club Code</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <BootstrapAlert variant="info">
            Custom club codes require admin approval and backend implementation.
            For now, try to map to the closest existing code.
          </BootstrapAlert>
          <Form.Group className="mb-3">
            <Form.Label>Club Code</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., H6, W11, etc."
              value={customClubName}
              onChange={(e) => setCustomClubName(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Category</Form.Label>
            <Form.Select
              value={customClubType}
              onChange={(e) => setCustomClubType(e.target.value)}
            >
              <option value="woods">Woods</option>
              <option value="hybrids">Hybrids</option>
              <option value="irons">Irons</option>
              <option value="wedges">Wedges</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCustomClubModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateCustomClub}>
            Submit Request
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ClubMapper;
